import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "react-toastify";
import {
  FaEnvelope,
  FaLock,
  FaEye,
  FaEyeSlash,
  FaUserShield,
  FaArrowLeft,
} from "react-icons/fa";
import { login, reset } from "../../features/auth/authSlice";

const AdminLogin = () => {
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [emailFocused, setEmailFocused] = useState(false);
  const [passwordFocused, setPasswordFocused] = useState(false);

  const { email, password } = formData;

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { user, isLoading, isError, isSuccess, message } = useSelector(
    (state) => state.auth,
  );

  useEffect(() => {
    if (isError) {
      toast.error(message);
    }

    if (isSuccess && user) {
      // Check if user is admin - handle different response structures
      // Backend returns { success: true, token, data: { role, ... } }
      const userRole = user?.data?.role || user?.user?.role || user?.role;
      if (
        userRole &&
        userRole !== "user" &&
        userRole !== "driver" &&
        userRole !== "vendor" &&
        userRole !== "corporate_admin"
      ) {
        navigate("/admin/dashboard");
      } else {
        toast.error("Access denied. Admin/Staff privileges required.");
        dispatch(reset());
      }
    }

    return () => {
      dispatch(reset());
    };
  }, [user, isError, isSuccess, message, navigate, dispatch]);

  const onChange = (e) => {
    setFormData((prevState) => ({
      ...prevState,
      [e.target.name]: e.target.value,
    }));
  };

  const onSubmit = (e) => {
    e.preventDefault();

    if (!email || !password) {
      toast.error("Please fill in all fields");
      return;
    }

    const userData = {
      email,
      password,
    };

    dispatch(login(userData));
  };

  const handleGoBack = () => {
    // Check if there is valid navigation history in this session
    const hasHistory = window.history.state && window.history.state.idx > 0;
    const isFromAdmin = document.referrer && document.referrer.includes("/admin");

    if (hasHistory && !isFromAdmin) {
      navigate(-1);
      // Safety fallback: if user is still on /admin/login after 150ms (e.g. redirected or blocked), return to home
      setTimeout(() => {
        if (window.location.pathname.includes("/admin/login")) {
          navigate("/");
        }
      }, 150);
    } else {
      navigate("/");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F8FAFC] via-[#F1F5F9] to-[#E2E8F0] relative flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 w-full overflow-hidden">
      {/* Decorative Blur Blobs */}
      <div className="absolute top-[-10%] left-[-10%] w-[500px] h-[500px] bg-blue-500/5 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute bottom-[-10%] right-[-10%] w-[500px] h-[500px] bg-indigo-500/5 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute top-[30%] right-[20%] w-[300px] h-[300px] bg-cyan-500/5 rounded-full blur-[100px] pointer-events-none" />

      {/* Grid background overlay for premium look */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#e2e8f0_1px,transparent_1px),linear-gradient(to_bottom,#e2e8f0_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_50%,#000_70%,transparent_100%)] opacity-[0.4] pointer-events-none" />

      <div className="w-full max-w-md relative z-10 space-y-8">
        <div className="flex flex-col items-center">
          {/* Logo Container with glowing effect */}
          <Link to="/" className="relative group cursor-pointer" title="Go to Website Home">
            <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-2xl blur opacity-25 group-hover:opacity-40 transition duration-1000 group-hover:duration-200" />
            <div className="relative h-16 w-16 bg-white border border-slate-200 rounded-2xl flex items-center justify-center shadow-md group-hover:scale-105 transition-transform">
              <FaUserShield className="h-8 w-8 text-blue-600 transition-colors" />
            </div>
          </Link>
          <h2 className="mt-6 text-center text-3xl font-extrabold tracking-tight text-slate-900">
            CabZigo Admin Portal
          </h2>
          <p className="mt-2 text-center text-sm text-slate-500">
            Sign in to access your dashboard and manage settings.
          </p>
        </div>

        {/* Login Form Container */}
        <div className="bg-white/80 backdrop-blur-xl border border-white/60 rounded-2xl shadow-xl p-8">
          <form className="space-y-6" onSubmit={onSubmit}>
            {/* Email Field */}
            <div>
              <label
                htmlFor="email"
                className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-2 px-1"
              >
                Email Address
              </label>
              <div className="relative rounded-xl transition-all duration-300">
                <div className={`absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none transition-colors duration-300 ${emailFocused || email ? "text-blue-600" : "text-slate-400"}`}>
                  <FaEnvelope className="h-5 w-5" />
                </div>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  className={`appearance-none block w-full pl-11 pr-4 py-3.5 bg-slate-50/50 border rounded-xl placeholder-slate-400 text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-500/50 transition-all duration-300 sm:text-sm ${
                    emailFocused 
                      ? "border-blue-500 bg-white shadow-[0_0_15px_rgba(59,130,246,0.08)]" 
                      : "border-slate-200 hover:border-slate-300"
                  }`}
                  placeholder="admin@cabzigo.com"
                  value={email}
                  onChange={onChange}
                  onFocus={() => setEmailFocused(true)}
                  onBlur={() => setEmailFocused(false)}
                />
              </div>
            </div>

            {/* Password Field */}
            <div>
              <div className="flex items-center justify-between mb-2 px-1">
                <label
                  htmlFor="password"
                  className="block text-xs font-bold text-slate-500 uppercase tracking-widest"
                >
                  Password
                </label>
                <div className="text-xs">
                  <Link to="/admin/forgot-password/send-otp" className="font-semibold text-blue-600 hover:text-blue-500 transition-colors">
                    Forgot your password?
                  </Link>
                </div>
              </div>
              <div className="relative rounded-xl transition-all duration-300">
                <div className={`absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none transition-colors duration-300 ${passwordFocused || password ? "text-blue-600" : "text-slate-400"}`}>
                  <FaLock className="h-5 w-5" />
                </div>
                <input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  required
                  className={`appearance-none block w-full pl-11 pr-11 py-3.5 bg-slate-50/50 border rounded-xl placeholder-slate-400 text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-500/50 transition-all duration-300 sm:text-sm ${
                    passwordFocused 
                      ? "border-blue-500 bg-white shadow-[0_0_15px_rgba(59,130,246,0.08)]" 
                      : "border-slate-200 hover:border-slate-300"
                  }`}
                  placeholder="••••••••"
                  value={password}
                  onChange={onChange}
                  onFocus={() => setPasswordFocused(true)}
                  onBlur={() => setPasswordFocused(false)}
                />
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                  <button
                    type="button"
                    className="text-slate-400 hover:text-slate-600 transition-colors focus:outline-none"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <FaEyeSlash className="h-5 w-5" />
                    ) : (
                      <FaEye className="h-5 w-5" />
                    )}
                  </button>
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div>
              <button
                type="submit"
                disabled={isLoading}
                className="group relative w-full flex justify-center py-3.5 px-4 border border-transparent text-sm font-bold rounded-xl text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-white focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition duration-200 shadow-lg shadow-blue-500/10 hover:shadow-blue-500/20 hover:-translate-y-0.5 active:translate-y-0"
              >
                {isLoading ? (
                  <div className="flex items-center gap-2">
                    <svg
                      className="animate-spin h-5 w-5 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Signing in...
                  </div>
                ) : (
                  "Sign In to Admin Portal"
                )}
              </button>
            </div>
          </form>

          {/* Divider and Back Link */}
          <div className="mt-6">
            <div className="relative flex items-center justify-center my-4">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-slate-200" />
              </div>
              <div className="relative flex justify-center text-xs">
                <span className="px-3 bg-white/70 backdrop-blur-md rounded-full text-slate-400 font-medium">or</span>
              </div>
            </div>

            <div className="mt-6 text-center">
              <button
                type="button"
                onClick={handleGoBack}
                className="inline-flex items-center gap-2 text-xs font-semibold text-slate-500 hover:text-slate-800 transition-colors duration-150 cursor-pointer"
                title="Go back to previous page or home"
              >
                <FaArrowLeft className="text-[10px]" />
                Go Back
              </button>
            </div>
          </div>
        </div>

        {/* Restriction Alert / Footer */}
        <div className="text-center bg-slate-100/60 border border-slate-200/50 rounded-xl p-4">
          <p className="text-xs text-slate-500 leading-relaxed">
            This is a restricted area. Only authorized administrators can access this portal. All login attempts are logged and monitored.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
